import { Clock } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const prokrastinacijaTest: QuizConfig = {
  id: "prokrastinacija-test",
  illustration: {
    src: "/images/quizzes/prokrastinacija-test.webp",
    alt: "Žena poseže za telefonom pored sveske sa nezapočetim zadatkom.",
  },
  lastModified: "2026-07-18",
  title: "Prokrastinacija: Zašto stalno odlažeš i kako da prestaneš?",
  shortTitle: "Prokrastinacija test",
  description:
    "Otkrij zašto odlažeš stvari i dobij konkretne savete kako da pobediš prokrastinaciju.",
  longDescription:
    "Ovaj test analizira tvoje obrasce odlaganja - kada, zašto i kako prokrastiniraš. Saznaćeš da li je tvoja prokrastinacija povezana sa strahom od neuspeha, perfekcionizmom ili nedostatkom motivacije. Na kraju dobijaš personalizovane strategije za prevazilaženje odlaganja i konkretne tehnike koje možeš primeniti odmah.",
  icon: Clock,
  duration: "3-5 min",
  category: "Lični razvoj",
  focusAreasWanted: [
    "procrastination",
    "motivation",
    "self_development",
    "emotional_regulation",
  ],
  questions: [
    {
      type: "radio",
      question: "Koliko često odlažeš važne zadatke?",
      options: ["Nikad", "Retko", "Ponekad", "Često", "Skoro uvek"],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kada imaš rok za nešto, kada obično počinješ da radiš?",
      options: [
        "Čim dobijem zadatak",
        "Sa dosta vremena unapred",
        "Negde na sredini",
        "Kad počne da gori",
        "U poslednjem trenutku",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Šta najčešće odlažeš? (označi sve što važi)",
      options: [
        "Poslovne obaveze",
        "Kućne poslove",
        "Zdravstvene preglede",
        "Teške razgovore",
        "Finansijske stvari (računi, porezi)",
        "Lične projekte i ciljeve",
        "Ništa posebno ne odlažem",
      ],
      scores: [1, 1, 1, 1, 1, 1, 0],
    },
    {
      type: "radio",
      question: "Kako se osećaš dok odlažeš nešto što treba da uradiš?",
      options: [
        "Opušteno, nije mi problem",
        "Malo mi smeta, ali izdržim",
        "Osećam nelagodu",
        "Stalno mi je u glavi i nervira me",
        "Užasno, ali i dalje ne počinjem",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko ti prokrastinacija utiče na svakodnevni život?",
      options: [
        "Ne utiče mi",
        "Minimalno",
        "Osrednje - ponekad pravi probleme",
        "Značajno - često trpim posledice",
        "Ozbiljno - uništava mi produktivnost i raspoloženje",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često sebi kažeš 'počeću sutra'?",
      options: ["Nikad", "Retko", "Ponekad", "Često", "To mi je mantra"],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question: "Šta te najčešće sprečava da počneš? (označi sve što važi)",
      options: [
        "Ne znam odakle da krenem",
        "Strah da neću dobro uraditi",
        "Zadatak mi je dosadan",
        "Previše toga imam",
        "Čekam 'pravi' trenutak ili motivaciju",
        "Lako me ometu druge stvari",
        "Ništa me posebno ne sprečava",
      ],
      scores: [1, 1, 1, 1, 1, 1, 0],
    },
    {
      type: "radio",
      question: "Koliko često završiš stvari na vreme?",
      options: ["Uvek", "Skoro uvek", "Uglavnom", "Retko", "Skoro nikad"],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "textarea",
      question:
        "Šta bi voleo/la da prestaneš da odlažeš? Kako bi ti život izgledao bez prokrastinacije?",
    },
    {
      type: "radio",
      question: "Da li si već probao/la neke tehnike protiv prokrastinacije?",
      options: [
        "Da, i pomažu mi",
        "Da, ali ne baš uspešno",
        "Pokušao/la sam jednom-dvaput",
        "Ne, ali razmišljam o tome",
        "Ne, ne znam ni odakle da počnem",
      ],
      scores: [0, 1, 2, 3, 4],
    },
  ],
  copy: {
    title: "Tvoj odnos sa prokrastinacijom",
    subtitle: "Analiza tvojih obrazaca odlaganja",
    scoreLabel: "Rezultat:",
    adviceTitle: "Kako da pobediš prokrastinaciju",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje",
    shareButtonText: "Podeli rezultat",
  },
  promptExtension: `
Kada interpretiraš rezultate ovog testa, uzmi u obzir:

1. TIPOVI PROKRASTINATORA na osnovu odgovora na pitanje o preprekama:
   - "Ne znam odakle da krenem" → Problem sa planiranjem, treba struktura
   - "Strah da neću dobro uraditi" → Perfekcionizam, strah od neuspeha
   - "Zadatak mi je dosadan" → Nedostatak motivacije, treba povezati sa vrednostima
   - "Previše toga imam" → Overwhelm, treba prioritizacija
   - "Čekam pravi trenutak" → Mit o motivaciji, treba akcija pre motivacije
   - "Lako me ometu" → Problem sa fokusom, treba eliminisati distrakcije

2. OZBILJNOST na osnovu skora:
   - 0-10: Minimalna prokrastinacija, zdrav odnos sa obavezama
   - 11-20: Umerena prokrastinacija, ima prostora za poboljšanje
   - 21-30: Izražena prokrastinacija, značajno utiče na život
   - 31+: Hronična prokrastinacija, možda je potrebna stručna pomoć

3. KONKRETNE TEHNIKE koje treba predložiti:
   - Pomodoro tehnika (25 min rada, 5 min pauze)
   - "Dva minuta pravilo" - ako traje manje od 2 min, uradi odmah
   - Razbijanje velikih zadataka na male korake
   - Eliminisanje distrakcija (telefon u drugoj sobi)
   - Accountability partner
   - Nagrađivanje sebe posle završenog zadatka

4. EMOCIONALNA KOMPONENTA:
   - Prokrastinacija je često strategija izbegavanja neprijatnih emocija
   - Validirati da je teško, ali da izbegavanje stvara više stresa
   - Ohrabriti self-compassion umesto samokritike
`,
};
